import type { StyleRule } from '@archipro-design/aria';
import {
    pxToRem,
    pxArrayToRem,
    labelClassName,
    buttonClassName,
    getScrollbarStyle,
} from '@archipro-design/aria';
import { dialogSlotClassNames } from '@fluentui/react-northstar';
import { hex2rgba } from '~/utils';

export const RefundPolicyContainer: StyleRule = ({ theme }) => ({
    paddingTop: pxToRem(9),
    [theme.screen.max.tablet]: {
        paddingTop: pxToRem(3),
    },
});

const MODAL_SPACING = pxToRem(48);

export const RefundPolicyDialog: StyleRule = ({ theme }) => ({
    maxHeight: '100vh !important',
    maxWidth: '100vw !important',
    borderRadius: '0 !important',
    overflow: 'hidden',
    [theme.screen.min.tablet]: {
        maxHeight: pxToRem(626),
    },
    [`& .${dialogSlotClassNames.content}`]: {
        border: 'none',
        margin: '0',
        height: '-webkit-fill-available',
        overflowX: 'hidden',
        overflowY: 'scroll',
        ...getScrollbarStyle(theme, '0'),
        padding: pxToRem(24),
        [theme.screen.min.tablet]: {
            marginTop: 0,
            padding: MODAL_SPACING,
            maxHeight: `calc(${pxToRem(626)} - ${MODAL_SPACING})`,
        },
    },
    [`& .${dialogSlotClassNames.header}`]: {
        height: pxToRem(62),
        width: '100%',
        borderBottom: `1px solid ${theme.siteVariables.colors.charcoal[250]}`,
        padding: pxArrayToRem([8, 24, 5]),
        [`& .${labelClassName}`]: {
            [theme.screen.laptop]: {
                lineHeight: theme.siteVariables.lineHeightDefault,
                fontSize: theme.siteVariables.fontSizes.subheading03,
                letterSpacing: theme.siteVariables.letterSpacingSmall,
            },
        },
    },
    [`& .${dialogSlotClassNames.footer}`]: {
        display: 'none',
    },
});

export const RefundPolicyDialogMobile: StyleRule = () => ({
    height: '100vh',
});

export const RefundHeader: StyleRule = () => ({
    width: '100%',
});

export const ReturnTriggerButton: StyleRule = ({ theme }) => ({
    padding: pxArrayToRem([12, 3, 0, 0]),
    lineHeight: theme.siteVariables.lineHeightMedium,
    textDecoration: 'underline',
    textUnderlineOffset: pxToRem(2),
    textDecorationSkipInk: 'none',
    color: theme.siteVariables.colors.grey['300'],
    [`&.${buttonClassName} .${labelClassName}`]: {
        fontSize: pxToRem(13),
        lineHeight: pxToRem(24),
        [theme.screen.tablet]: {
            fontSize: pxToRem(16),
            lineHeight: pxToRem(30),
        },
    },

    '& > div': {
        letterSpacing: pxToRem(0.1),
    },
    [theme.screen.max.tablet]: {
        [`& .${labelClassName}`]: {
            fontSize: theme.siteVariables.fontSizes.caption04,
            lineHeight: 1.25,
        },
    },
});

export const ReturnTemplate: StyleRule = ({ theme }) => ({
    fontSize: theme.siteVariables.fontSizes.caption04,
    padding: pxArrayToRem([0, 18, 0, 18]),
    lineHeight: theme.siteVariables.lineHeightMedium,
    letterSpacing: theme.siteVariables.letterSpacingMedium,
    color: theme.siteVariables.colors.charcoal['300'],
    maxWidth: pxToRem(800),
    display: 'block',

    '& h2': {
        ...theme.siteVariables.textStyles.Display.Medium65,
        fontSize: theme.siteVariables.fontSizes.caption04,
        color: theme.siteVariables.colors.charcoal['300'],
        lineHeight: theme.siteVariables.lineHeightSmallest,
        letterSpacing: pxToRem(0.04),
        margin: pxArrayToRem([0, 0, 19, 0]),
        display: 'block',

        [theme.screen.min.tablet]: {
            fontSize: theme.siteVariables.fontSizes.subheading02,
            lineHeight: '175%',
            marginBottom: pxToRem(9),
            display: 'inline-block',
        },
    },

    '& p': {
        margin: pxArrayToRem([16, 0, 0, 0]),
        padding: 0,
        fontSize: theme.siteVariables.fontSizes.caption04,
        lineHeight: theme.siteVariables.lineHeightMedium,
        display: 'block',
        [theme.screen.min.tablet]: {
            fontSize: theme.siteVariables.fontSizes.caption02,
        },
    },

    '& ol': {
        marginBottom: theme.siteVariables.spaces['space-24'],
        marginLeft: pxToRem(-24),
        display: 'block',
    },

    '& ul': {
        marginBottom: theme.siteVariables.spaces['space-24'],
        marginLeft: pxToRem(-24),
        display: 'block',
    },
    '& a, a:active, a:visited': {
        color: theme.siteVariables.colors.charcoal['300'],
        textDecoration: 'underline',
    },
    '& a:hover': {
        backgroundColor: hex2rgba(
            theme.siteVariables.colors.primitive.black,
            0.06
        ),
    },
    [theme.screen.min.tablet]: {
        fontSize: theme.siteVariables.fontSizes.caption02,
        padding: 0,
        '& div': {
            '& > *': {
                lineHeight: '143%',
            },
        },
    },
});

export const BoldPolicyContent: StyleRule = ({ theme }) => ({
    ...theme.siteVariables.textStyles.Heading.Bold75,
    textTransform: 'uppercase',
});

export const PolicyNotice: StyleRule = ({ theme }) => ({
    display: 'block',
    marginBottom: pxToRem(16),
    lineHeight: theme.siteVariables.lineHeightSmaller,
    letterSpacing: pxToRem(0.02),
    ...theme.siteVariables.textStyles.Text.Roman55,
});

export const PolicyContact: StyleRule = ({ theme }) => ({
    ...theme.siteVariables.textStyles.Heading.Bold75,
    textTransform: 'uppercase',
    fontSize: theme.siteVariables.fontSizes.caption04,
    marginBottom: pxToRem(16),
    [theme.screen.min.tablet]: {
        fontSize: theme.siteVariables.fontSizes.caption02,
    },
});
